import { FC } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  faGripVertical,
  faArrowDown,
  faArrowUp,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { twMerge } from '@lib/tailwind-merge'
import { secondsToHHMM } from '@utils/time'
import { taskSourceCode } from '@utils/taskSourceCode'
import { EntryWithId } from './types'
import { IconButton } from 'components/buttons'

interface Props {
  entry: EntryWithId
  position: number
  isEditable: boolean
  onMoveUp?: () => void
  onMoveDown?: () => void
  canMoveUp: boolean
  canMoveDown: boolean
}

export const DraggableEntry: FC<Props> = ({
  entry,
  position,
  isEditable,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: entry.id,
      disabled: !isEditable,
    })

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center justify-between">
        <span className="text-sm font-medium text-neutral-400">{position}</span>
        {isEditable && (
          <div className="flex flex-row text-neutral-600">
            <IconButton
              onClick={onMoveUp}
              disabled={!canMoveUp}
              icon={faArrowUp}
              iconProps={{ size: 'sm' }}
            />
            <IconButton
              onClick={onMoveDown}
              disabled={!canMoveDown}
              icon={faArrowDown}
              iconProps={{ size: 'sm' }}
            />
          </div>
        )}
      </div>
      <div
        ref={setNodeRef}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
        }}
        className={twMerge(
          'flex flex-row items-center gap-4 p-4 bg-neutral-200 rounded text-neutral-900 pointer-none cursor-default',
          isEditable && 'cursor-grab active:cursor-grabbing active:shadow z-20',
        )}
        {...attributes}
        {...listeners}
      >
        {isEditable && (
          <div className="">
            <FontAwesomeIcon icon={faGripVertical} />
          </div>
        )}
        <div className="flex flex-col grow gap-1 font-medium">
          <div className="flex flex-row items-center gap-1 text-sm">
            <span>{entry.task.name}</span>
            <span className="font-normal">
              ({secondsToHHMM(entry.duration)})
            </span>
          </div>
          <div className="text-xs text-neutral-600">
            {[
              entry.task.project.name,
              taskSourceCode(entry.task.projectIntegration),
              ...entry.task.displayPath,
            ]
              .filter(Boolean)
              .join(' • ')}
          </div>
        </div>
      </div>
    </div>
  )
}
